import type {
  Receipt,
  ReceiptConfirmResponse,
  ReceiptLine,
  ReceiptLineUpdateInput,
} from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { Ban, Link2, Link2Off, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { useGeneration } from '@/features/ai/use-generation';
import { useCatalogBoot, useCatalogStores } from '@/features/catalog/hooks';
import { storeLabel } from '@/features/catalog/store-label';
import { ProductForm } from '@/features/catalog/product-form';
import { formatMoney, formatMoneyDelta } from '@/features/prices/hooks';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeWorkerError, toastError } from '@/lib/errors';
import { currentFormatLocale } from '@/lib/i18n';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';

const receiptKeys = queryKeys.receipts;

const NO_STORE = '__none__';

const isMatched = (line: ReceiptLine): boolean =>
  line.matchStatus === 'CONFIRMED' ||
  (line.matchStatus === 'PROPOSED' &&
    (line.matchConfidence === 'EXACT' || line.matchConfidence === 'LIKELY'));

/** Pick what a printed line means: an item of the trip, a product, a new product – or nothing. */
function MatchDialog({
  receipt,
  line,
  onClose,
  onChange,
}: {
  receipt: Receipt;
  line: ReceiptLine;
  onClose: () => void;
  onChange: (patch: ReceiptLineUpdateInput) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const listId = receipt.shoppingListId ?? '';
  const items = useLiveQuery(
    () => (listId ? offlineDb.shoppingItems.where('listId').equals(listId).toArray() : []),
    [listId],
  );
  const products = useLiveQuery(() => offlineDb.catalogProducts.toArray(), []);
  const needle = query.trim().toLowerCase();
  const bought = (items ?? [])
    .map((row) => row.item)
    .filter((item) => item.checked && (!needle || item.name.toLowerCase().includes(needle)));
  const found = (products ?? [])
    .map((row) => row.product)
    .filter(
      (product) =>
        !needle || `${product.brand ?? ''} ${product.name}`.toLowerCase().includes(needle),
    )
    .slice(0, 30);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        closeLabel={t('common.close')}
        className="max-h-[90dvh] overflow-y-auto"
        data-testid="receipt-match-dialog"
      >
        <DialogHeader>
          <DialogTitle>{line.rawText}</DialogTitle>
          <DialogDescription>{t('receipts.match.description')}</DialogDescription>
        </DialogHeader>

        {creating ? (
          <ProductForm
            onCancel={() => setCreating(false)}
            onSaved={(product) => onChange({ productId: product.id })}
          />
        ) : (
          <div className="space-y-4">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('receipts.match.search')}
              aria-label={t('receipts.match.search')}
              data-testid="receipt-match-search"
            />
            {bought.length > 0 && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  {t('receipts.match.items')}
                </h3>
                <ul className="divide-y rounded-lg border text-sm">
                  {bought.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="min-h-[44px] w-full px-3 text-left"
                        onClick={() =>
                          onChange({
                            shoppingListItemId: item.id,
                            productId: item.product?.id ?? null,
                            ingredientId: item.product ? null : (item.ingredientId ?? null),
                          })
                        }
                        data-testid="receipt-match-item"
                      >
                        {item.name}
                        {item.product && (
                          <span className="block text-xs text-muted-foreground">
                            {item.product.name}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                {t('receipts.match.products')}
              </h3>
              <ul className="divide-y rounded-lg border text-sm">
                {found.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="min-h-[44px] w-full px-3 text-left"
                      onClick={() => onChange({ productId: product.id })}
                      data-testid="receipt-match-product"
                    >
                      {product.brand ? `${product.brand} ${product.name}` : product.name}
                    </button>
                  </li>
                ))}
                {found.length === 0 && (
                  <li className="p-3 text-muted-foreground">{t('receipts.match.noProducts')}</li>
                )}
              </ul>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setCreating(true)}>
                <Plus aria-hidden /> {t('receipts.match.newProduct')}
              </Button>
              <Button
                variant="outline"
                onClick={() => onChange({ ignored: true })}
                data-testid="receipt-match-ignore"
              >
                <Ban aria-hidden /> {t('receipts.match.ignore')}
              </Button>
              {(line.product || line.ingredient || line.shoppingListItem) && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    onChange({ productId: null, ingredientId: null, shoppingListItemId: null })
                  }
                >
                  <Link2Off aria-hidden /> {t('receipts.match.remove')}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LineRow({
  line,
  attached,
  currency,
  editable,
  onPick,
}: {
  line: ReceiptLine;
  attached: ReceiptLine[];
  currency: string;
  editable: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const target = line.product
    ? line.product.brand
      ? `${line.product.brand} ${line.product.name}`
      : line.product.name
    : (line.ingredient?.name ?? line.shoppingListItem?.name ?? null);
  const suggestion = line.matchStatus === 'PROPOSED' && line.matchConfidence === 'POSSIBLE';
  return (
    <li
      className={cn('space-y-1 p-3', line.matchStatus === 'IGNORED' && 'opacity-60')}
      data-testid="receipt-line"
      data-status={line.matchStatus}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 font-mono text-sm">
          {line.quantity && line.quantity !== '1' && `${line.quantity} × `}
          {line.rawText}
          {line.weight && ` · ${line.weight} ${line.weightUnitCode ?? ''}`}
        </span>
        <span className="shrink-0 text-sm tabular-nums">
          {formatMoneyDelta(line.totalPrice, currency).replace(/^\+/, '')}
        </span>
      </div>
      {attached.map((extra) => (
        <p key={extra.id} className="text-xs text-muted-foreground">
          {t(`receipts.kind.${extra.kind}`)}: {extra.rawText} ·{' '}
          {formatMoneyDelta(extra.totalPrice, currency).replace(/^\+/, '')}
        </p>
      ))}
      <button
        type="button"
        disabled={!editable}
        onClick={onPick}
        className={cn(
          'inline-flex min-h-[36px] items-center gap-1 rounded-full border px-3 text-xs',
          !target && 'border-dashed',
        )}
        data-testid="receipt-line-match"
      >
        <Link2 aria-hidden className="size-3.5" />
        {line.matchStatus === 'IGNORED'
          ? t('receipts.review.ignored')
          : target
            ? suggestion
              ? t('receipts.review.suggestion', { name: target })
              : target
            : t('receipts.review.unmatched')}
      </button>
      {line.shoppingListItem && line.product && (
        <span className="ml-2 text-xs text-muted-foreground">
          {t('receipts.review.forItem', { name: line.shoppingListItem.name })}
        </span>
      )}
    </li>
  );
}

/**
 * The review of a receipt (docs/partial/shopping-in-store-spec.md 5.4): the only dense screen.
 * One printed line per row, the match as a chip that opens a picker, unmatched lines on top,
 * a sticky footer with the count and Confirm. Deposit and discounts hang on the line they
 * belong to – they are never offered as products.
 */
export function ReceiptReviewPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useCatalogBoot();
  const stores = useCatalogStores();
  const [picking, setPicking] = useState<ReceiptLine | null>(null);
  const [summary, setSummary] = useState<ReceiptConfirmResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const query = useQuery({
    queryKey: receiptKeys.detail(id),
    queryFn: () => api.receipts.get(id),
    refetchInterval: (state) => (state.state.data?.status === 'PROCESSING' ? 2000 : false),
  });
  const receipt = query.data ?? null;
  const { generation } = useGeneration(
    receipt?.status === 'PROCESSING' ? receipt.generationId : null,
  );
  const store = (next: Receipt) => {
    queryClient.setQueryData(receiptKeys.detail(id), next);
    void queryClient.invalidateQueries({ queryKey: receiptKeys.all, exact: true });
  };

  const updateLine = useMutation({
    mutationFn: (input: { lineId: string; patch: ReceiptLineUpdateInput }) =>
      api.receipts.updateLine(id, input.lineId, input.patch),
    onSuccess: (next) => {
      store(next);
      setPicking(null);
    },
    onError: (e) => toastError(e),
  });
  const updateHeader = useMutation({
    mutationFn: (storeId: string | null) => api.receipts.update(id, { storeId }),
    onSuccess: store,
    onError: (e) => toastError(e),
  });
  const confirm = useMutation({
    mutationFn: () => api.receipts.confirm(id, crypto.randomUUID()),
    onSuccess: (result) => {
      store(result.receipt);
      setSummary(result);
    },
    onError: (e) => toastError(e),
  });
  const deleteImages = useMutation({
    mutationFn: () => api.receipts.deleteImages(id),
    onSuccess: (next) => {
      store(next);
      toast.success(t('receipts.review.photosDeleted'));
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.receipts.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      navigate('/shopping/receipts', { replace: true });
    },
    onError: (e) => toastError(e),
  });

  const { main, attachedTo } = useMemo(() => {
    const lines = receipt?.lines ?? [];
    const byParent = new Map<string, ReceiptLine[]>();
    for (const line of lines)
      if (line.refersToLineId)
        byParent.set(line.refersToLineId, [...(byParent.get(line.refersToLineId) ?? []), line]);
    // Unmatched first: they are what the user came for.
    const rank = (line: ReceiptLine) =>
      line.kind !== 'ITEM' ? 3 : line.matchStatus === 'IGNORED' ? 2 : isMatched(line) ? 1 : 0;
    return {
      main: lines
        .filter((line) => !line.refersToLineId)
        .sort((a, b) => rank(a) - rank(b) || a.position - b.position),
      attachedTo: (line: ReceiptLine) => byParent.get(line.id) ?? [],
    };
  }, [receipt]);

  if (query.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (!receipt)
    return (
      <PageContainer>
        <Alert variant="destructive">{t('receipts.notFound')}</Alert>
      </PageContainer>
    );

  const currency = receipt.currency ?? 'EUR';
  const reviewable = receipt.status === 'REVIEW' || receipt.status === 'CONFIRMED';
  const editable = receipt.editable && reviewable;
  const purchasedAt = receipt.purchasedAt
    ? new Intl.DateTimeFormat(currentFormatLocale(), {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC',
      }).format(new Date(receipt.purchasedAt))
    : null;

  return (
    <PageContainer className="max-w-3xl pb-24">
      <FeaturePageHeader
        title={receipt.store?.name ?? receipt.storeNameRaw ?? t('receipts.unknownStore')}
        description={[purchasedAt, receipt.total && formatMoney(receipt.total, currency)]
          .filter(Boolean)
          .join(' · ')}
        backTo="/shopping/receipts"
        actions={
          <Badge variant={receipt.status === 'CONFIRMED' ? 'muted' : 'secondary'}>
            {t(`receipts.status.${receipt.status}`)}
          </Badge>
        }
      />

      {receipt.status === 'PROCESSING' && (
        <ProcessingIndicator
          label={t('receipts.review.reading')}
          progress={generation?.progress ?? null}
        />
      )}
      {receipt.status === 'FAILED' && (
        <Alert variant="destructive" data-testid="receipt-failed">
          {describeWorkerError(receipt.errorCode)}
          {receipt.duplicateOfId && (
            <>
              {' '}
              <Link className="underline" to={`/shopping/receipts/${receipt.duplicateOfId}`}>
                {t('receipts.review.openFirst')}
              </Link>
            </>
          )}
        </Alert>
      )}
      {reviewable && receipt.sumMismatch && (
        <Alert variant="warning" className="mb-3" data-testid="receipt-sum-mismatch">
          {t('receipts.review.sumMismatch')}
        </Alert>
      )}

      {summary && (
        <Alert className="mb-4" data-testid="receipt-summary">
          <p className="font-medium">
            {t('receipts.summary.matched', {
              matched: summary.receipt.matchedLineCount,
              total: summary.receipt.lineCount,
            })}
            {summary.receipt.total && ` · ${formatMoney(summary.receipt.total, currency)}`}
          </p>
          <p className="text-sm">
            {t('receipts.summary.created', {
              prices: summary.observations,
              items: summary.itemsPriced,
            })}
          </p>
          {summary.receipt.estimatedTotal && (
            <p className="text-sm" data-testid="receipt-estimated">
              {t('receipts.summary.estimated', {
                estimated: formatMoney(summary.receipt.estimatedTotal, currency),
              })}
            </p>
          )}
          {summary.increases.length > 0 && (
            <div className="mt-2 text-sm" data-testid="receipt-increases">
              <p>{t('receipts.summary.increases', { count: summary.increases.length })}</p>
              <ul className="list-disc pl-5">
                {summary.increases.map((change) => (
                  <li key={`${change.productId}-${change.since}`}>
                    {change.productName}: {formatMoney(change.previous.price, currency)} →{' '}
                    {formatMoney(change.current.price, currency)} (
                    {formatMoneyDelta(change.absolute, currency)})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Alert>
      )}

      {reviewable && (
        <>
          <div className="mb-4 max-w-sm">
            <Label htmlFor="receipt-store">{t('prices.dialog.store')}</Label>
            <Select
              value={receipt.store?.id ?? NO_STORE}
              onValueChange={(value) => updateHeader.mutate(value === NO_STORE ? null : value)}
              disabled={!editable || updateHeader.isPending}
            >
              <SelectTrigger id="receipt-store" className="mt-1" data-testid="receipt-store">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STORE}>
                  {receipt.storeNameRaw
                    ? t('receipts.review.storeAsPrinted', { name: receipt.storeNameRaw })
                    : t('prices.dialog.somewhere')}
                </SelectItem>
                {stores.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {storeLabel(candidate)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!receipt.store && (
              <p className="mt-1 text-xs text-muted-foreground">{t('receipts.review.storeHint')}</p>
            )}
          </div>

          {receipt.images.length > 0 && (
            <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="receipt-photos">
              {receipt.images.map((image, index) =>
                image.url ? (
                  <a key={image.assetId} href={image.url} target="_blank" rel="noreferrer">
                    <img
                      src={image.url}
                      alt={t('receipts.capture.part', { number: index + 1 })}
                      className="h-24 w-auto rounded border object-cover"
                    />
                  </a>
                ) : null,
              )}
              {receipt.mine && (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={deleteImages.isPending}
                  onClick={() => deleteImages.mutate()}
                  data-testid="receipt-delete-photos"
                >
                  <Trash2 aria-hidden /> {t('receipts.review.deletePhotos')}
                </Button>
              )}
            </div>
          )}

          <ul className="divide-y rounded-lg border" data-testid="receipt-lines">
            {main.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                attached={attachedTo(line)}
                currency={currency}
                editable={editable && line.kind === 'ITEM'}
                onPick={() => setPicking(line)}
              />
            ))}
          </ul>
        </>
      )}

      <div className="mt-4 flex justify-end">
        {(receipt.mine || receipt.editable) && (
          <Button variant="ghost" onClick={() => setDeleting(true)} data-testid="receipt-delete">
            <Trash2 aria-hidden /> {t('receipts.review.delete')}
          </Button>
        )}
      </div>

      {reviewable && (
        // On a phone the main navigation owns the bottom edge: the footer sits on top of it.
        <div className="fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 border-t bg-background/95 p-3 backdrop-blur md:bottom-0">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <span className="text-sm" data-testid="receipt-matched-count">
              {t('receipts.matched', {
                matched: receipt.matchedLineCount,
                total: receipt.lineCount,
              })}
            </span>
            <Button
              disabled={!editable || receipt.lineCount === 0}
              loading={confirm.isPending}
              onClick={() => confirm.mutate()}
              data-testid="receipt-confirm"
            >
              {receipt.status === 'CONFIRMED'
                ? t('receipts.review.confirmAgain')
                : t('receipts.review.confirm')}
            </Button>
          </div>
        </div>
      )}

      {picking && (
        <MatchDialog
          receipt={receipt}
          line={picking}
          onClose={() => setPicking(null)}
          onChange={(patch) => updateLine.mutate({ lineId: picking.id, patch })}
        />
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('receipts.review.deleteTitle')}
        description={t('receipts.review.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </PageContainer>
  );
}
